import React, { useState, useRef, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Alert } from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/functions";
import { useAuth } from "hooks/useAuth";
import useMfaStatus from "hooks/useMfaStatus";
import Loading from "components/Loading";
import AuthFlow, { STEPS } from "components/auth/AuthFlow";
import AuthPageShell, { AUTH_HERO_TITLE } from "components/auth/AuthPageShell";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { isEmailVerifiedForApp } from "utils/isDevAuthFirestoreUser";
import { roleRequiresMfa } from "utils/mfaPolicy";
import { isLearnerSignup } from "features/morningReport/learnerSignup";

/**
 * AuthPage - Unified sign-in / sign-up page (email, Google, Apple).
 *
 * Supports ?role=patient|provider|facility param to pre-select account type.
 * When fully signed in (role, onboarding done, email verified), redirects to
 * dashboard, or in prod to onboarding if MFA is not yet enrolled.
 */
const AuthPage = () => {
  const { user: authUser, userData, userLoading } = useAuth();
  // ⚠️ AN ANONYMOUS GUEST IS SIGNED OUT, as far as this page is concerned. Public pages
  // (the discharge QR page, CareStart, VisitStart) sign visitors in anonymously, and
  // their "sign in" links land here with that session still live. Counted as a user,
  // it has no email and no verification, so this page parked them on "We sent a
  // verification link to ." with the address blank. Signing in or signing up from
  // here replaces the anonymous session, which is what those pages expect.
  const user = authUser && !authUser.isAnonymous ? authUser : null;
  const { isEnrolled, sessionMfaLoading } = useMfaStatus();
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get("role");
  // Arrived from the email-verification link on a device with no session.
  const justVerified = searchParams.get("verified") === "1";

  const [idleLogout] = useState(() => {
    const reason = sessionStorage.getItem("hd_logout_reason");
    if (reason === "idle") {
      sessionStorage.removeItem("hd_logout_reason");
      return true;
    }
    return false;
  });

  // Email captured at idle logout — lets us skip the email step and land the
  // user on the password step with it prefilled. Read once, then cleared.
  const [idleEmail] = useState(() => {
    const email = sessionStorage.getItem("hd_logout_email");
    if (email) {
      sessionStorage.removeItem("hd_logout_email");
      return email;
    }
    return null;
  });

  // The share code travels in the URL so provider onboarding survives a device or
  // browser switch (e.g. tapping the verification link in a phone mail app, which
  // opens a fresh context with empty localStorage). The URL is the source of
  // truth; localStorage is just a same-device cache we re-seed from it below.
  const shareCode = (searchParams.get("c") || "").replace(/\D/g, "");
  const isValidShareCode = shareCode.length === 7;

  useEffect(() => {
    if (!isValidShareCode) return;
    localStorage.setItem("pendingShareReturn", `/share?c=${shareCode}`);
    if (preselectedRole === "provider") {
      localStorage.setItem("pendingSignupRole", "provider");
    }
  }, [isValidShareCode, shareCode, preselectedRole]);

  // Invitation-only signup has ONE deliberate exception: a provider who arrived
  // via a patient's share link — proven by a valid 7-digit code in the URL, or the
  // localStorage breadcrumbs ShareLandingPage leaves on the same device. Public
  // /auth?role=provider (MainMenu, /register/provider) stays invite-gated.
  const fromShareProviderSignup =
    (preselectedRole === "provider" && isValidShareCode) ||
    (typeof window !== "undefined" &&
      (localStorage.getItem("pendingSignupRole") === "provider" ||
        !!localStorage.getItem("pendingShareReturn")));
  // The second exception: MorningReport learners. A learner account sees only the
  // simulated teaching cases (no patient data), so the /morning-report landing page
  // may sign anyone up. It leaves this hint (and ?role=learner) on the way here.
  const fromLearnerSignup =
    preselectedRole === "learner" ||
    (typeof window !== "undefined" &&
      localStorage.getItem("pendingSignupRole") === "learner");

  const signupInProgressRef = useRef(false);
  const inviteAcceptingRef = useRef(false);
  const [inviteAccepting, setInviteAccepting] = useState(false);
  const [inviteTokenCleared, setInviteTokenCleared] = useState(false);
  const [invitePrefilledEmail, setInvitePrefilledEmail] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(
    Boolean(sessionStorage.getItem("pendingOrganizationInviteToken")),
  );
  const [emailVerifiedRefreshLoading, setEmailVerifiedRefreshLoading] =
    useState(false);
  const [emailVerifiedOverride, setEmailVerifiedOverride] = useState(null);
  const [isOnSuccessStep, setIsOnSuccessStep] = useState(false);
  const pendingInviteToken = !inviteTokenCleared
    ? sessionStorage.getItem("pendingOrganizationInviteToken")
    : null;
  // @dev.local accounts never receive a real verification email; treat them as
  // verified the same way DashboardPage and useAuth already do.
  const effectiveEmailVerified =
    emailVerifiedOverride === null
      ? isEmailVerifiedForApp(user, userData)
      : emailVerifiedOverride;
  const learnerSignup = isLearnerSignup({
    urlRole: preselectedRole,
    hasInvite: Boolean(pendingInviteToken),
    hasShareCode: fromShareProviderSignup,
  });

  useEffect(() => {
    let cancelled = false;

    const refreshEmailVerified = async () => {
      if (!user || invitePrefilledEmail) {
        setEmailVerifiedOverride(null);
        return;
      }

      if (isEmailVerifiedForApp(user, userData)) {
        setEmailVerifiedOverride(true);
        return;
      }

      setEmailVerifiedRefreshLoading(true);
      try {
        await firebase.auth().currentUser?.reload();
        if (cancelled) return;
        const reloaded = firebase.auth().currentUser;
        setEmailVerifiedOverride(isEmailVerifiedForApp(reloaded, userData));
      } catch (err) {
        if (cancelled) return;
        setEmailVerifiedOverride(isEmailVerifiedForApp(user, userData));
      } finally {
        if (!cancelled) setEmailVerifiedRefreshLoading(false);
      }
    };

    refreshEmailVerified();
    return () => {
      cancelled = true;
    };
  }, [user, userData, invitePrefilledEmail]);

  const acceptInviteForCurrentUser = async () => {
    if (!pendingInviteToken) return;
    const acceptInvitation = firebase
      .functions()
      .httpsCallable("acceptInvitation");
    try {
      await acceptInvitation({ token: pendingInviteToken });
      sessionStorage.removeItem("pendingOrganizationInviteToken");
      setInviteTokenCleared(true);
    } catch (err) {
      const code = err?.code || "";
      const message = (err?.message || "").toLowerCase();
      const isTerminal =
        code.includes("already-exists") ||
        code.includes("permission-denied") ||
        code.includes("not-found") ||
        (code.includes("failed-precondition") &&
          (message.includes("accepted") ||
            message.includes("revoked") ||
            message.includes("expired") ||
            message.includes("different email") ||
            message.includes("organization")));
      if (isTerminal) {
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        setInviteTokenCleared(true);
      } else {
        throw err;
      }
    }
  };

  useEffect(() => {
    const loadInviteContext = async () => {
      if (!pendingInviteToken || user) {
        setInviteLoading(false);
        return;
      }
      setInviteLoading(true);
      try {
        const getInvitation = firebase
          .functions()
          .httpsCallable("getInvitation");
        const result = await getInvitation({ token: pendingInviteToken });
        const invitedEmail = result?.data?.invitation?.email || null;
        if (invitedEmail) setInvitePrefilledEmail(invitedEmail);
      } catch (err) {
        // Token is stale/invalid; clear it so auth works normally.
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        setInviteTokenCleared(true);
      } finally {
        setInviteLoading(false);
      }
    };

    loadInviteContext();
  }, [pendingInviteToken, user]);

  useEffect(() => {
    const acceptPendingInvite = async () => {
      if (!pendingInviteToken || !user || !userData) return;
      if (inviteAcceptingRef.current) return;

      // Nothing to do once user is already in an org.
      if (userData.organizationId) {
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        setInviteTokenCleared(true);
        return;
      }

      // Wait until account setup is done and role is set.
      if (userData.onboarding || userData.role !== "professional") return;

      inviteAcceptingRef.current = true;
      setInviteAccepting(true);
      try {
        await acceptInviteForCurrentUser();
      } catch (err) {
        // Keep token for retry path below.
      } finally {
        inviteAcceptingRef.current = false;
        setInviteAccepting(false);
      }
    };

    acceptPendingInvite();
  }, [pendingInviteToken, user, userData]);

  if (
    user &&
    userData?.role &&
    !userData?.onboarding &&
    (isEnrolled ||
      shouldSkipMfaEnrollmentUi() ||
      !roleRequiresMfa(userData?.role)) &&
    effectiveEmailVerified &&
    !signupInProgressRef.current
  ) {
    return <Navigate to="/dashboard" />;
  }

  const shouldShowLoading =
    userLoading ||
    inviteLoading ||
    inviteAccepting ||
    (user && userData === undefined) || // userData not yet fetched from Firestore
    (user && (sessionMfaLoading || emailVerifiedRefreshLoading));

  if (shouldShowLoading) {
    return <Loading page />;
  }

  const shouldRequireEmailVerification =
    user &&
    !effectiveEmailVerified &&
    !invitePrefilledEmail &&
    !signupInProgressRef.current;

  const initialStep = shouldRequireEmailVerification
    ? STEPS.EMAIL_VERIFY
    : undefined;

  if (user && !shouldRequireEmailVerification && !signupInProgressRef.current) {
    return <Navigate to="/onboarding" />;
  }

  return (
    <AuthPageShell logoLinked={!isOnSuccessStep}>
      <AuthFlow
        preselectedRole={preselectedRole}
        topNotice={
          idleLogout ? (
            <Alert severity="info" sx={{ mt: -1, mb: 3 }}>
              You were signed out due to inactivity.
            </Alert>
          ) : justVerified && !user ? (
            <Alert severity="info" sx={{ mt: -1, mb: 3 }}>
              Your email is verified. Please sign in to continue.
            </Alert>
          ) : null
        }
        // isLearnerSignup also opens signup on morningreport.si itself, where a
        // visitor may reach /auth without the landing page's hint.
        allowSignup={fromShareProviderSignup || fromLearnerSignup || learnerSignup}
        signupRole={learnerSignup ? "learner" : null}
        shareCode={isValidShareCode ? shareCode : null}
        prefilledEmail={
          invitePrefilledEmail ||
          idleEmail ||
          (shouldRequireEmailVerification ? user?.email : null)
        }
        disableOAuth={!!invitePrefilledEmail}
        skipEmailVerification={!!invitePrefilledEmail}
        initialStep={initialStep}
        onSignupStart={() => {
          signupInProgressRef.current = true;
        }}
        onSuccessStep={setIsOnSuccessStep}
        heroTitle={AUTH_HERO_TITLE}
        inlineBack
      />
    </AuthPageShell>
  );
};

export default AuthPage;
